import { useState } from 'react';
import { DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton, Spinner, SpinnerSize, TextField } from '@fluentui/react';
import {
    Options16Filled, ArrowSync16Filled, ArrowExport16Filled, Clipboard16Filled,
    ThumbLike16Filled, ThumbDislike16Filled, DividerTall24Filled, Play16Filled, Stop16Filled,
} from "@fluentui/react-icons";

import styles from "./Answer.module.css";

interface Props {
    onSettingsClick: () => void;
    onRegenerateClick: () => void;
    onExportClick: () => void;
    onCopyAnswerClick: () => void;
    onFeedbackSubmitted: (sentiment: string, comments?: string) => void;
    toggleAudioPlay: () => void;
    isAudioLoading: boolean;
    isAudioPlaying: boolean;
    isFeedbackSubmitted: boolean;
    isStreaming?: boolean;
    useDarkMode: boolean;
};

export const AnswerOptionsPanel = ({
    onSettingsClick,
    onRegenerateClick,
    onExportClick,
    onCopyAnswerClick,
    onFeedbackSubmitted,
    toggleAudioPlay,
    isAudioLoading,
    isAudioPlaying,
    isFeedbackSubmitted,
    isStreaming,
    useDarkMode
}: Props) => {
    const [isCopied, setIsCopied] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
    const [isFeedbackSaving, setIsFeedbackSaving] = useState(false);
    const [feedbackSentiment, setFeedbackSentiment] = useState('');
    const [feedbackComments, setFeedbackComments] = useState('');

    const getAudioIcon = () => {
        if (isAudioLoading) {
            return <Spinner size={SpinnerSize.small} />;
        }

        return isAudioPlaying
            ? <Stop16Filled className={styles.adjustInputIcon} />
            : <Play16Filled className={styles.adjustInputIcon} />;
    };

    const handleExportClick = async () => {
        if (isExporting) {
            // Return if already exporting
            return;
        };

        if (onExportClick) {
            setIsExporting(true);
            try {
                onExportClick();
            } catch (error) {
                console.error("Error during export: ", error);
            }
            setIsExporting(false);
        }
    };

    const handleCopyClick = () => {
        if (onCopyAnswerClick) {
            onCopyAnswerClick();
        }

        setIsCopied(true);
        setTimeout(() => {
            setIsCopied(false);
        }, 2000); // Change back after 2 seconds
    };

    const handleSentimentClick = async (sentiment: string) => {
        setFeedbackSentiment(sentiment);
        if (sentiment == "negative") {
            setIsFeedbackDialogOpen(true);
        }
        else {
            onFeedbackSubmitted(sentiment);
        }
    };

    const handleSubmitFeedback = async () => {
        setIsFeedbackSaving(true);
        onFeedbackSubmitted(feedbackSentiment, feedbackComments);
        setIsFeedbackDialogOpen(false);
        setIsFeedbackSaving(false);
    };

    return (
        <div className={styles.adjustInputContainer}>
            <div className={`${useDarkMode ? styles.adjustInputDark : styles.adjustInput} ${isStreaming ? styles.disabled : ''}`}
                onClick={isStreaming ? undefined : onSettingsClick}>
                <Options16Filled className={styles.adjustInputIcon} />
                <span className={styles.adjustInputText}>Settings</span>
            </div>
            <div className={`${useDarkMode ? styles.adjustInputDark : styles.adjustInput} ${isStreaming ? styles.disabled : ''}`}
                onClick={isStreaming ? undefined : onRegenerateClick}>
                <ArrowSync16Filled className={styles.adjustInputIcon} />
                <span className={styles.adjustInputText}>Regenerate</span>
            </div>
            <div className={`${useDarkMode ? styles.adjustInputDark : styles.adjustInput} ${isStreaming ? styles.disabled : ''}`}
                onClick={isStreaming ? undefined : handleExportClick}>
                <ArrowExport16Filled className={styles.adjustInputIcon} />
                <span className={styles.adjustInputText}>
                    {isExporting ? 'Please wait...' : 'Export'}
                </span>
            </div>
            <div className={`${useDarkMode ? styles.adjustInputDark : styles.adjustInput} ${isStreaming ? styles.disabled : ''}`}
                onClick={isStreaming ? undefined : handleCopyClick}>
                <Clipboard16Filled className={styles.adjustInputIcon} />
                <span className={styles.adjustInputText}>
                    {isCopied ? 'Copied!' : 'Copy'}
                </span>
            </div>
            <div className={useDarkMode ? styles.adjustDividerDark : styles.adjustDivider}>
                <DividerTall24Filled />
            </div>
            {!isFeedbackSubmitted ?
                <>
                    <div className={`${useDarkMode ? styles.adjustInputDark : styles.adjustInput} ${isStreaming ? styles.disabled : ''}`}
                        onClick={isStreaming ? undefined : () => handleSentimentClick("positive")}
                        title="Good Response">
                        <ThumbLike16Filled className={styles.adjustInputIcon} />
                    </div>
                    <div className={`${useDarkMode ? styles.adjustInputDark : styles.adjustInput} ${isStreaming ? styles.disabled : ''}`}
                        onClick={isStreaming ? undefined : () => handleSentimentClick("negative")}
                        title="Bad Response">
                        <ThumbDislike16Filled className={styles.adjustInputIcon} />
                    </div>
                </>
                :
                <span className={styles.adjustInputText}>
                    Feedback Submitted
                </span>
            }
            <div className={useDarkMode ? styles.adjustDividerDark : styles.adjustDivider}>
                <DividerTall24Filled />
            </div>
            <div
                className={`${useDarkMode ? styles.adjustInputDark : styles.adjustInput} ${isStreaming || isAudioLoading ? styles.disabled : ''}`}
                onClick={isStreaming || isAudioLoading ? undefined : toggleAudioPlay}
                title={isAudioLoading ? "Loading" : isAudioPlaying ? "Stop" : "Play"}
            >
                {getAudioIcon()}
            </div>

            <Dialog
                hidden={!isFeedbackDialogOpen}
                onDismiss={() => setIsFeedbackDialogOpen(false)}
                modalProps={{ isBlocking: true, }}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: 'Submit Feedback'
                }}>
                Please provide any additional feedback you may have
                <TextField
                    multiline
                    rows={5}
                    disabled={isFeedbackSaving}
                    onChange={(e, newValue) => setFeedbackComments(newValue || '')} />
                <DialogFooter>
                    <PrimaryButton
                        iconProps={{ iconName: 'Send' }}
                        className={styles.panelButton}
                        disabled={isFeedbackSaving}
                        onClick={handleSubmitFeedback}
                        text="Submit"
                    />
                    <DefaultButton
                        iconProps={{ iconName: 'Cancel' }}
                        className={styles.panelButton}
                        disabled={isFeedbackSaving}
                        onClick={() => setIsFeedbackDialogOpen(false)}
                        text="Cancel"
                    />
                </DialogFooter>
            </Dialog>
        </div>
    );
}